/**
 * 学习板块 TTS「听书」原生桥（Android 自研 TtsEngine 插件；Web/no-native 全 no-op）
 * 沿用 screenGuard.ts 范式：命令走原生桥全局 Capacitor.nativePromise('TtsEngine', method, opts)；
 * 事件（逐句完成/错误、锁屏播控动作）走 Capacitor.Plugins.TtsEngine 动态代理的 addListener。
 * 不引任何 npm 插件、apps/web 零新增依赖；非原生环境调用静默返回。
 */

/** 锁屏/通知栏媒体动作 */
export type TtsTransportAction = 'play' | 'pause' | 'next' | 'previous' | 'stop';

interface PluginListenerHandle {
  remove: () => Promise<void>;
}

interface TtsEngineProxy {
  addListener?: (eventName: string, listenerFunc: (data: any) => void) => Promise<PluginListenerHandle>;
  [key: string]: unknown;
}

interface CapacitorGlobal {
  isNativePlatform?: () => boolean;
  nativePromise?: (pluginId: string, methodName: string, options?: Record<string, unknown>) => Promise<unknown>;
  registerPlugin?: (pluginName: string) => TtsEngineProxy;
  Plugins?: Record<string, TtsEngineProxy>;
}

function capacitor(): CapacitorGlobal | null {
  return (globalThis as { Capacitor?: CapacitorGlobal }).Capacitor ?? null;
}

/** 是否处于可驱动原生 TTS 的环境（原生平台 + 桥可用） */
export function isTtsNative(): boolean {
  const cap = capacitor();
  return cap?.isNativePlatform?.() === true && typeof cap.nativePromise === 'function';
}

function call<T = unknown>(method: string, options?: Record<string, unknown>): Promise<T | null> {
  const cap = capacitor();
  if (!isTtsNative() || !cap?.nativePromise) return Promise.resolve(null);
  return cap.nativePromise('TtsEngine', method, options).then((r) => r as T).catch((e) => {
    console.warn(`[tts] ${method} 失败：`, e);
    return null;
  });
}

/** 首次播放前申请通知权限（Android 13+ 前台服务通知需要；其余平台 no-op） */
export function ttsEnsurePermissions(): Promise<unknown> {
  return call('ensurePermissions');
}

/** 初始化引擎并探测中文可用性 → { supported } */
export function ttsInitialize(lang = 'zh-CN'): Promise<{ supported?: boolean } | null> {
  return call<{ supported?: boolean }>('initialize', { lang });
}

/** 中文语音是否可用（引擎已装且支持 zh-CN） */
export function ttsIsChineseSupported(): Promise<boolean> {
  return call<{ supported?: boolean }>('isChineseSupported').then((r) => r?.supported === true);
}

export interface TtsSpeakOptions {
  text: string;
  /** 客户端生成的唯一 utteranceId，用于把完成事件与当前句对应（防陈旧事件误推进） */
  id: string;
  rate?: number;
  pitch?: number;
  volume?: number;
  lang?: string;
  /** 试听模式：原生不拉前台服务/通知 */
  preview?: boolean;
}

/** 朗读一句（入队即 resolve，真正播完通过 onTtsChunk 事件回报） */
export function ttsSpeak(opts: TtsSpeakOptions): Promise<unknown> {
  return call('speak', opts as unknown as Record<string, unknown>);
}

export interface TtsSpeakQueueOptions {
  /** 整章句子数组，原生侧按序逐句朗读 */
  texts: string[];
  /** 与 texts 一一对应的 utteranceId，用于完成事件对位与陈旧过滤 */
  ids: string[];
  rate?: number;
  lang?: string;
}

/**
 * 整章逐句队列朗读：一次性把整章句子交给原生，由原生 UtteranceProgressListener.onDone
 * 在原生线程直接念下一句，熄屏/后台 WebView 被冻结时也不断音；JS 仅收进度与队列完成事件。
 */
export function ttsSpeakQueue(opts: TtsSpeakQueueOptions): Promise<unknown> {
  return call(
    'speakQueue',
    opts as unknown as Record<string, unknown>,
  );
}

export function ttsStop(): Promise<unknown> {
  return call('stop');
}

export function ttsSetRate(rate: number): Promise<unknown> {
  return call('setRate', { rate });
}

/** 设备 TTS 引擎的一个可选音色 */
export interface TtsVoice {
  name: string;
  locale: string;
  quality?: number;
  default?: boolean;
}

/** 列出可用音色（name 为空表示引擎未就绪/不支持） */
export function ttsGetVoices(): Promise<TtsVoice[]> {
  return call<{ voices?: TtsVoice[] }>('getVoices').then((r) => (Array.isArray(r?.voices) ? r!.voices! : []));
}

/** 设置朗读音色（传 null/空→跟随系统默认）；name+locale+quality 三字段联合定位，实际生效于下一句 speak */
export function ttsSetVoice(v: { name: string; locale: string; quality?: number } | null): Promise<unknown> {
  if (!v || !v.name) return call('setVoice', {});
  return call('setVoice', { name: v.name, locale: v.locale || '', quality: v.quality ?? -1 });
}

/** 设备上已安装的一个 TTS 引擎 */
export interface TtsEngineInfo {
  package: string;
  name: string;
  default?: boolean;
}

/** 列出可用 TTS 引擎（不同引擎音色差异大） */
export function ttsGetEngines(): Promise<TtsEngineInfo[]> {
  return call<{ engines?: TtsEngineInfo[] }>('getEngines').then((r) => (Array.isArray(r?.engines) ? r!.engines! : []));
}

/** 切换 TTS 引擎（null/空→系统默认）；切后需重新 getVoices */
export function ttsSetEngine(pkg: string | null): Promise<unknown> {
  return call('setEngine', { package: pkg ?? '' });
}

/** 更新锁屏/通知栏标题（当前章节名） */
export function ttsUpdateMetadata(title: string): Promise<unknown> {
  return call('updateMetadata', { title });
}

/** 结束播放并回收前台服务 */
export function ttsRelease(): Promise<unknown> {
  return call('release');
}

/**
 * 取 TtsEngine 插件代理：TtsEngine 为纯原生插件（无 JS shim），Capacitor.Plugins 只由各插件 JS 侧 registerPlugin 填充，
 * 故首次需显式 Capacitor.registerPlugin('TtsEngine') 触发代理构建（读 PluginHeaders 接原生），否则事件 addListener 永远拿不到。
 */
function ttsProxy(): TtsEngineProxy | undefined {
  const cap = capacitor();
  if (!cap) return undefined;
  try {
    if (!cap.Plugins?.TtsEngine && typeof cap.registerPlugin === 'function') {
      cap.registerPlugin('TtsEngine');
    }
    return cap.Plugins?.TtsEngine;
  } catch {
    return undefined;
  }
}

function addListener(event: string, cb: (data: any) => void): () => void {
  const proxy = ttsProxy();
  if (!proxy?.addListener) return () => undefined;
  let handle: PluginListenerHandle | null = null;
  let disposed = false;
  try {
    void Promise.resolve(proxy.addListener(event, cb))
      .then((h) => {
        if (disposed) void h?.remove?.();
        else handle = h;
      })
      .catch(() => undefined);
  } catch {
    return () => undefined;
  }
  return () => {
    disposed = true;
    void handle?.remove?.().catch?.(() => undefined);
  };
}

export interface TtsChunkEvent {
  type: 'done' | 'error';
  id: string;
  error?: string;
}

/** 逐句播放完成/出错事件（原生 UtteranceProgressListener 转发） */
export function onTtsChunk(cb: (e: TtsChunkEvent) => void): () => void {
  const offDone = addListener('ttsChunkDone', (d: { id?: string }) => {
    if (d?.id) cb({ type: 'done', id: d.id });
  });
  const offErr = addListener('ttsChunkError', (d: { id?: string; error?: string }) => {
    if (d?.id) cb({ type: 'error', id: d.id, error: d.error });
  });
  return () => {
    offDone();
    offErr();
  };
}

/** 整章队列念完事件（原生念到最后一句后回报，携带末句 id 供对位） */
export interface TtsQueueDoneEvent {
  lastId: string;
}

export function onTtsQueueDone(cb: (e: TtsQueueDoneEvent) => void): () => void {
  return addListener('ttsQueueDone', (d: { lastId?: string }) => {
    cb({ lastId: d?.lastId ?? '' });
  });
}

/** 锁屏/通知栏/耳机播控动作事件（play/pause/next/previous/stop） */
export function onTtsTransport(cb: (action: TtsTransportAction) => void): () => void {
  return addListener('ttsTransport', (d: { action?: TtsTransportAction }) => {
    if (d?.action) cb(d.action);
  });
}
